import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { RADIUS_OPTIONS } from 'constants/filterOptions';
import RpDistanceFilter from 'components/commons/RpDistanceFilter';
import useFilterStore from 'zustandStore/useFilterStore';
import { SALES, LISTINGS, RENTALS } from 'constants/comparableTypes';
import Colors from 'constants/colors';
import { validateDistance } from 'lib/Validations';

function DistanceFilter(props) {
    const filters = useFilterStore(state => state.filters);
    const isDefault = useFilterStore(state => state.isDefaultFilterValue);
    const setFiltersForVariations = useFilterStore(state => state.setFiltersForVariations);
    const setError = useFilterStore(state => state.setError);
    const removeError = useFilterStore(state => state.removeError);
    const [hasError, setHasError] = useState(false);

    const validateDistanceValue = (val) => {
        return parseFloat(val) > 100 && '*Please input a value not greater than 100km';
    };

    const formatRadiusValue = (radius) => {
        if (!radius) return '';
        const radiusStr = String(radius);
        const km = radiusStr.slice(-2);
        if (km.toLowerCase() !== 'km') {
            return `${radiusStr}Km`;
        }
        return radiusStr.toLowerCase().replace('km', 'Km');
    };

    const value = React.useMemo(() => {
        const distance = filters[props.comparableType]?.distance ?? 1;
        const foundOption = RADIUS_OPTIONS.find(opt => opt.value === distance);

        if (foundOption) {
            return foundOption;
        }

        return {
            label: hasError ? distance : formatRadiusValue(distance),
            value: distance
        };
    }, [filters, props.comparableType, hasError]);

    const handleChange = (newValue) => {
        const distanceValue = typeof newValue === 'string' ? newValue : newValue?.value;
        const variations = props.comparableType === RENTALS ? [RENTALS] : [SALES, LISTINGS];
        setFiltersForVariations(variations, { distance: distanceValue });
    };

    const handleInputChange = (e) => {
        const value = e.target.value;

        const decimalIndex = value.indexOf('.');
        if (decimalIndex !== -1) {
            const decimalPlaces = value.length - decimalIndex - 1;
            if (decimalPlaces > 2) {
                e.target.value = value.slice(0, decimalIndex + 3);
            }
        }
    };

    const validateInput = (input) => {
        return (
            validateDistance(input) ||
            validateDistanceValue(input)
        );
    };

    const onError = (error) => {
        const hasErrors = Boolean(error.length);
        setHasError(hasErrors);
        if (hasErrors) {
            setError(props.comparableType, 'distance');
        } else {
            removeError(props.comparableType, 'distance');
        }
    };

    return (
        <RpDistanceFilter
            options={RADIUS_OPTIONS}
            value={value}
            onChange={handleChange}
            onInputChange={handleInputChange}
            label={props.label || 'Distance'}
            tooltip="Locate properties within a specified distance from the target property, either by selecting an option from the dropdown or manually enter in a value."
            active={!isDefault(props.comparableType, 'distance', value.value)}
            inputStyle={{
                '& .MuiFormLabel-root': {
                    '&.Mui-focused': {
                        color: Colors.PRIMARY.MAIN,
                    },
                },
                '& .MuiOutlinedInput-root': {
                    '&.Mui-focused .MuiInputBase-input': {
                        color: Colors.PRIMARY.MAIN,
                    },
                }
            }}
            containerStyle={props.containerStyle}
            validateInput={validateInput}
            onError={onError}
        />
    );
}

DistanceFilter.propTypes = {
    comparableType: PropTypes.string.isRequired,
    label: PropTypes.string,
    containerStyle: PropTypes.object,
};

export default DistanceFilter;